// src/scenes/restaurant/Locations/Locations.jsx
//
// Suivi des locations de chambres. Le check-IN se fait à la réception depuis
// l'app mobile (c'est elle qui imprime le ticket et programme l'alerte de fin
// de moment) ; l'admin sert au suivi, au check-out de rattrapage et à
// l'encaissement du solde.
//
// Rappels du modèle :
//   - tout se paie à l'entrée → au check-out il ne reste que les services
//     consommés (note de chambre) et les nuits supplémentaires
//   - `montant_total` n'est calculé QU'AU check-out : avant, il vaut 0
//   - `heure_fin_prevue` / `temps_restant_secondes` ne concernent que le moment
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Button,
  Typography, Dialog, DialogTitle, DialogContent, DialogActions,
  MenuItem, Chip, Card, CardContent, CircularProgress,
  FormControl, InputLabel, Select, TextField,
  Tab, Tabs, Alert, Tooltip, Divider, IconButton,
} from '@mui/material';
// MUI v7 : le `Grid` par défaut a perdu les props `item` / `xs` / `md`
// au profit de `size={{...}}`. Ce fichier utilise l'ancienne API.
import { GridLegacy as Grid } from '@mui/material';
import {
  Hotel, Search, Logout, Payments, Refresh, Warning, AccessTime,
} from '@mui/icons-material';
import { locationAPI, chambreAPI } from '../../../services/restaurantAPI';
import { toast } from 'react-toastify';

const TYPES = {
  moment: 'Moment',
  nuit: 'Nuit',
  sejour: 'Séjour',
};

const STATUTS = {
  en_cours: { label: 'En cours', color: 'warning' },
  terminee: { label: 'Terminée', color: 'success' },
  annulee: { label: 'Annulée', color: 'error' },
};

const fmt = (n) => new Intl.NumberFormat('fr-HT', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
}).format(Number(n) || 0) + ' HTG';

const fmtDateHeure = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
};

/** Décompte lisible d'un forfait moment. null si ce n'est pas un moment. */
const tempsRestant = (location) => {
  if (location.type_location !== 'moment' || !location.heure_fin_prevue) return null;
  if (location.statut !== 'en_cours') return null;
  const secondes = Math.round((new Date(location.heure_fin_prevue).getTime() - Date.now()) / 1000);
  if (secondes <= 0) return { texte: 'Temps écoulé', depasse: true };
  const h = Math.floor(secondes / 3600);
  const m = Math.floor((secondes % 3600) / 60);
  return { texte: h > 0 ? `${h} h ${String(m).padStart(2, '0')}` : `${m} min`, depasse: false };
};

const versTableau = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  return [];
};

const messageErreur = (error, defaut) => {
  const d = error.response?.data;
  if (!d) return error.message || defaut;
  if (typeof d === 'string') return d;
  if (d.error) return d.error;
  if (d.detail) return d.detail;
  const premier = Object.entries(d)[0];
  if (premier) {
    const [champ, valeur] = premier;
    return `${champ} : ${Array.isArray(valeur) ? valeur[0] : valeur}`;
  }
  return defaut;
};

const LocationsPage = () => {
  const [locations, setLocations] = useState([]);
  const [chambres, setChambres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtreChambre, setFiltreChambre] = useState('all');
  const [filtreType, setFiltreType] = useState('all');
  const [activeTab, setActiveTab] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  // Location en cours de check-out : { location, etape: 'confirmation'|'paiement' }
  const [checkout, setCheckout] = useState(null);
  // Rafraîchit le décompte des moments sans recharger l'API
  const [, setTick] = useState(0);

  const fetchTout = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [rLoc, rCh] = await Promise.all([
        locationAPI.getAll(),
        chambreAPI.getAll(),
      ]);
      setLocations(versTableau(rLoc.data));
      setChambres(versTableau(rCh.data));
    } catch (err) {
      const msg = messageErreur(err, 'Erreur lors du chargement des locations');
      setError(msg);
      toast.error(msg);
      setLocations([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTout(); }, [fetchTout]);

  // Le décompte des moments doit avancer tout seul
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  const chambresById = useMemo(
    () => new Map(chambres.map((c) => [c.id, c.numero])),
    [chambres],
  );

  const handleCheckout = async () => {
    setSubmitting(true);
    try {
      const { data } = await locationAPI.checkOut(checkout.location.id);
      const reste = Number(data.reste_a_payer ?? data.montant_total) || 0;
      if (reste > 0) {
        setCheckout({ location: data, etape: 'paiement' });
        toast.info('Chambre libérée — il reste un solde à encaisser');
      } else {
        setCheckout(null);
        toast.success('Check-out effectué, rien à encaisser');
      }
      fetchTout();
    } catch (err) {
      toast.error(messageErreur(err, 'Erreur lors du check-out'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEncaisser = async () => {
    const reste = Number(checkout.location.reste_a_payer ?? checkout.location.montant_total) || 0;
    setSubmitting(true);
    try {
      await locationAPI.payer(checkout.location.id, reste);
      toast.success(`${fmt(reste)} encaissé`);
      setCheckout(null);
      fetchTout();
    } catch (err) {
      toast.error(messageErreur(err, 'Erreur lors du paiement'));
    } finally {
      setSubmitting(false);
    }
  };

  const filtrees = useMemo(() => {
    const recherche = searchTerm.toLowerCase();
    return locations.filter((l) => {
      const numero = l.chambre_numero || chambresById.get(l.chambre) || '';
      const correspondRecherche =
        l.nom_client?.toLowerCase().includes(recherche) ||
        String(numero).toLowerCase().includes(recherche) ||
        false;
      const correspondChambre = filtreChambre === 'all' || l.chambre === Number(filtreChambre);
      const correspondType = filtreType === 'all' || l.type_location === filtreType;
      const correspondStatut = activeTab === 0 ? l.statut === 'en_cours' : l.statut !== 'en_cours';
      return correspondRecherche && correspondChambre && correspondType && correspondStatut;
    });
  }, [locations, searchTerm, filtreChambre, filtreType, activeTab, chambresById]);

  const stats = useMemo(() => {
    const enCours = locations.filter((l) => l.statut === 'en_cours');
    const terminees = locations.filter((l) => l.statut === 'terminee');
    return {
      enCours: enCours.length,
      depasses: enCours.filter((l) => tempsRestant(l)?.depasse).length,
      services: enCours.reduce((s, l) => s + (Number(l.montant_services) || 0), 0),
      revenus: terminees.reduce((s, l) => s + (Number(l.montant_total) || 0), 0),
    };
  }, [locations]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert
          severity="error"
          icon={<Warning />}
          sx={{ borderRadius: 2 }}
          action={<Button variant="outlined" size="small" onClick={fetchTout}>Réessayer</Button>}
        >
          <Typography variant="h6" gutterBottom>Erreur de chargement</Typography>
          <Typography>{error}</Typography>
        </Alert>
      </Box>
    );
  }

  const resteAPayer = checkout
    ? Number(checkout.location.reste_a_payer ?? checkout.location.montant_total) || 0
    : 0;

  return (
    <Box sx={{ p: 3, backgroundColor: 'background.default', minHeight: '100vh' }}>
      <Box mb={4}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h4" gutterBottom fontWeight="bold">
              <Hotel sx={{ mr: 2, verticalAlign: 'middle' }} />
              Locations
            </Typography>
            <Typography color="textSecondary" variant="body1">
              Chambres occupées et historique des séjours
            </Typography>
          </Grid>
          <Grid item xs={12} md={6} display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
            <TextField
              placeholder="Client ou n° de chambre..."
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} /> }}
              sx={{ width: 280 }}
            />
            <Tooltip title="Rafraîchir">
              <IconButton onClick={fetchTout}><Refresh /></IconButton>
            </Tooltip>
          </Grid>
        </Grid>

        <Grid container spacing={3} mt={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: 2, height: '100%' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">Locations en cours</Typography>
                <Typography variant="h4" fontWeight="bold" color="warning.main">{stats.enCours}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: 2, height: '100%' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">Moments dépassés</Typography>
                <Typography
                  variant="h4"
                  fontWeight="bold"
                  color={stats.depasses > 0 ? 'error.main' : 'text.primary'}
                >
                  {stats.depasses}
                </Typography>
                <Typography variant="caption" color="textSecondary">à faire sortir</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: 2, height: '100%' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">Services sur note</Typography>
                <Typography variant="h4" fontWeight="bold">{fmt(stats.services)}</Typography>
                <Typography variant="caption" color="textSecondary">à encaisser à la sortie</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: 2, height: '100%' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">Revenus (terminées)</Typography>
                <Typography variant="h4" fontWeight="bold" color="success.main">{fmt(stats.revenus)}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Box display="flex" gap={2} flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Chambre</InputLabel>
            <Select value={filtreChambre} label="Chambre" onChange={(e) => setFiltreChambre(e.target.value)}>
              <MenuItem value="all">Toutes les chambres</MenuItem>
              {chambres.map((c) => (
                <MenuItem key={c.id} value={c.id}>Chambre {c.numero}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Formule</InputLabel>
            <Select value={filtreType} label="Formule" onChange={(e) => setFiltreType(e.target.value)}>
              <MenuItem value="all">Toutes</MenuItem>
              {Object.entries(TYPES).map(([cle, label]) => (
                <MenuItem key={cle} value={cle}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ '& .MuiTab-root': { minHeight: 48 } }}>
          <Tab label="En cours" />
          <Tab label="Historique" />
        </Tabs>
      </Box>

      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Chambre</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Client</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Formule</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Entrée</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{activeTab === 0 ? 'Temps restant' : 'Sortie'}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">Prix</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">Services</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">
                  {activeTab === 0 ? 'Payé' : 'Total'}
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Statut</TableCell>
                {activeTab === 0 && <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtrees.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={activeTab === 0 ? 10 : 9} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="textSecondary">
                      {activeTab === 0 ? 'Aucune chambre occupée' : 'Aucune location terminée'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filtrees.map((location) => {
                  const statut = STATUTS[location.statut] || { label: location.statut, color: 'default' };
                  const decompte = tempsRestant(location);
                  const services = Number(location.montant_services) || 0;
                  return (
                    <TableRow
                      key={location.id}
                      hover
                      sx={decompte?.depasse ? { backgroundColor: 'rgba(239,68,68,0.06)' } : undefined}
                    >
                      <TableCell>
                        <Typography fontWeight="bold">
                          {location.chambre_numero || chambresById.get(location.chambre) || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{location.nom_client || '—'}</Typography>
                        {location.telephone_client && (
                          <Typography variant="caption" color="textSecondary">
                            {location.telephone_client}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip label={TYPES[location.type_location] || location.type_location} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{fmtDateHeure(location.heure_debut)}</Typography>
                      </TableCell>
                      <TableCell>
                        {activeTab === 0 ? (
                          decompte ? (
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <AccessTime fontSize="small" color={decompte.depasse ? 'error' : 'action'} />
                              <Typography
                                variant="body2"
                                fontWeight={decompte.depasse ? 'bold' : 'normal'}
                                color={decompte.depasse ? 'error.main' : 'text.primary'}
                              >
                                {decompte.texte}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="textSecondary">—</Typography>
                          )
                        ) : (
                          <Typography variant="body2">{fmtDateHeure(location.heure_fin)}</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">{fmt(location.prix_convenu)}</TableCell>
                      <TableCell align="right">
                        {services > 0
                          ? <Typography color="warning.main" fontWeight="medium">{fmt(services)}</Typography>
                          : <Typography variant="body2" color="textSecondary">—</Typography>}
                      </TableCell>
                      <TableCell align="right">
                        {activeTab === 0 ? (
                          <Typography color="success.main">{fmt(location.montant_paye)}</Typography>
                        ) : (
                          <Typography fontWeight="bold">{fmt(location.montant_total)}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip label={statut.label} size="small" color={statut.color} variant="outlined" />
                      </TableCell>
                      {activeTab === 0 && (
                        <TableCell align="center">
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<Logout />}
                            onClick={() => setCheckout({ location, etape: 'confirmation' })}
                          >
                            Check-out
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Check-out puis encaissement du solde */}
      <Dialog
        open={!!checkout}
        onClose={() => !submitting && setCheckout(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        {checkout && (
          <>
            <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Box display="flex" alignItems="center" gap={1}>
                {checkout.etape === 'confirmation' ? <Logout /> : <Payments />}
                <Typography variant="h6">
                  {checkout.etape === 'confirmation' ? 'Confirmer le check-out' : 'Encaisser le solde'}
                </Typography>
              </Box>
            </DialogTitle>

            <DialogContent dividers>
              {checkout.etape === 'confirmation' ? (
                <>
                  <Box display="flex" justifyContent="space-between" py={0.5}>
                    <Typography color="text.secondary">Chambre</Typography>
                    <Typography fontWeight="medium">
                      {checkout.location.chambre_numero || chambresById.get(checkout.location.chambre)}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" py={0.5}>
                    <Typography color="text.secondary">Client</Typography>
                    <Typography fontWeight="medium">{checkout.location.nom_client || '—'}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" py={0.5}>
                    <Typography color="text.secondary">Formule</Typography>
                    <Typography fontWeight="medium">
                      {TYPES[checkout.location.type_location] || checkout.location.type_location}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" py={0.5}>
                    <Typography color="text.secondary">Entrée</Typography>
                    <Typography fontWeight="medium">{fmtDateHeure(checkout.location.heure_debut)}</Typography>
                  </Box>

                  <Divider sx={{ my: 1.5 }} />

                  <Box display="flex" justifyContent="space-between" py={0.5}>
                    <Typography color="text.secondary">Prix convenu</Typography>
                    <Typography fontWeight="medium">{fmt(checkout.location.prix_convenu)}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" py={0.5}>
                    <Typography color="text.secondary">Payé à l'entrée</Typography>
                    <Typography fontWeight="medium" color="success.main">
                      {fmt(checkout.location.montant_paye)}
                    </Typography>
                  </Box>
                  {Number(checkout.location.montant_services) > 0 && (
                    <Box display="flex" justifyContent="space-between" py={0.5}>
                      <Typography color="text.secondary">Services consommés</Typography>
                      <Typography fontWeight="medium" color="warning.main">
                        + {fmt(checkout.location.montant_services)}
                      </Typography>
                    </Box>
                  )}

                  <Alert severity="info" sx={{ mt: 2 }}>
                    Le montant final est calculé par le serveur à la confirmation de la sortie.
                    La chambre repasse en disponible.
                  </Alert>
                </>
              ) : (
                <>
                  <Typography variant="h3" textAlign="center" fontWeight="bold" py={2}>
                    {fmt(resteAPayer)}
                  </Typography>
                  <Typography color="text.secondary" textAlign="center">
                    Solde restant après le paiement de l'entrée : services consommés
                    et nuits supplémentaires.
                  </Typography>
                </>
              )}
            </DialogContent>

            <DialogActions sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
              <Button onClick={() => !submitting && setCheckout(null)} variant="outlined" disabled={submitting}>
                {checkout.etape === 'confirmation' ? 'Annuler' : 'Plus tard'}
              </Button>
              {checkout.etape === 'confirmation' ? (
                <Button
                  onClick={handleCheckout}
                  variant="contained"
                  color="error"
                  disabled={submitting}
                  startIcon={submitting ? <CircularProgress size={20} /> : <Logout />}
                >
                  Confirmer le check-out
                </Button>
              ) : (
                <Button
                  onClick={handleEncaisser}
                  variant="contained"
                  color="success"
                  disabled={submitting}
                  startIcon={submitting ? <CircularProgress size={20} /> : <Payments />}
                >
                  Encaisser {fmt(resteAPayer)}
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default LocationsPage;
